<!-- Complex: never unmount the layout on chain switch — a heterodimer chain refetch briefly empties annotations(), which would destroy the sidebar and the Mol* container. -->
@let hasAnythingToShow = isComplex() ? chainCards().length > 0 : inRangeAnnotations().length > 0;

@if (!hasAnythingToShow && !isComplex() && activeChainAnnotationsLoading()) {
  <div class="tab-annotations-loading" aria-label="Loading annotations">
    <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A" stroke="#707372" fill="#707372" />
    </svg>
  </div>
} @else if (!hasAnythingToShow) {
  <div class="no-domain-parent">
    <div class="no-domain-information">No annotations available</div>
    <div class="no-domain-notes">Annotations will appear here as new data is added in future updates.</div>
  </div>
}

@if (hasAnythingToShow) {
  <div>
    <section class="info-banner">
      <p>
        This page shows sentences extracted from open access publications that refer to specific residues in this
        structure. Annotations are identified automatically using AI methods developed by the PDBe team.
        <br/><a href="/faq#faq-45" target="_blank">Learn about our method...</a>
      </p>
    </section>

    @if (downloadAllFailed()) {
      <div>
        <app-status-message (closeLinkClick)="downloadAllFailed.set(false)" messageText="Download failed due to a technical issue. Please refresh and try again." messageType="error">
        </app-status-message>
      </div>
    }

    <main class="tab-parent" [class.change-display]="!isSidebarVisible()">
      @if (isSidebarVisible()) {
        <interactive-left-table
          tabName="TextAnnotations"
          [tedDomainData]="chainCards()"
          (handleSelectedCard)="onCardClick($event)">
          <div sidebarHeader class="download-all-row">
            <button
              type="button"
              class="annotations-download-link"
              [disabled]="isDownloadingAll()"
              [attr.aria-busy]="isDownloadingAll()"
              (click)="downloadAllCsv()">
              Download annotations
              @if (isDownloadingAll()) {
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Preparing download">
                  <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A" stroke="#707372" fill="#707372" />
                </svg>
              } @else {
                <img src="assets/img/download-nested.svg" alt="" width="18" height="18" />
              }
            </button>
          </div>
        </interactive-left-table>
      }
      <div class="tab-content">
        @if (isComplex()) {
          @let icon = isSidebarDisplayed() ? 'left' : 'right'; @let text = isSidebarDisplayed() ? 'Open full-width' : 'Show text annotations';
          <button class="vf-button-ghost" (click)="toggleSidebar()">
            <img [src]="'assets/img/two_arrows_' + icon + '.png'" alt="arrow icons" width="16" height="16" />
            <span class="openclosetext">{{ text }}</span>
          </button>
        }

        @if (entryData()) {
          @let uniprotAccession = activeCard()?.uniprotAccession ?? entryData()?.uniprotAccession;
          <h5 class="domain-title">
            <strong>{{ activeCard()?.displayTitle ?? entryData()?.uniprotDescription ?? entryData()?.uniprotAccession }}</strong>
          </h5>

          <section class="domain-info">
            <div class="info-row">
              <span>Source organism</span>
              <article>
                @if (entryData()?.organismScientificName) {
                  <i>{{ entryData()?.organismScientificName }}</i>
                } @else {
                  <span>Not available</span>
                }
              </article>
            </div>
            <div class="info-row">
              <span>UniProt</span>
              <article>
                @if (uniprotAccession) {
                  {{ uniprotAccession }}
                  @if (activeUniprotRange(); as range) {
                    @if (range.start !== 1) {
                      [residues: {{ range.start }} – {{ range.end }}]
                    }
                  }
                  <a [href]="'https://www.uniprot.org/uniprotkb/' + uniprotAccession" target="_blank">go to UniProt</a>
                } @else {
                  <span>Not available</span>
                }
              </article>
            </div>
            @if (showAnnotatedResiduesRow()) {
              <div class="info-row">
                <span>Annotated residues</span>
                <article>
                  @if (activeChainAnnotationsLoading()) {
                    <svg class="annotated-residues-loading" width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Loading annotated residues">
                      <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A" stroke="#707372" fill="#707372" />
                    </svg>
                  } @else {
                    {{ annotatedResiduesCount() }}
                  }
                </article>
              </div>
            }
          </section>
        }

        <div class="chain-title-row">
          @if (!showChainDropdown()) {
            <span class="chaintext">{{ getActiveChainTitle() }}</span>
          }
          @if (showChainDropdown()) {
            <entry-dropdown
              [downloadable]="false"
              [islink]="false"
              [options]="chainDropdownOptions()"
              [title]="getActiveChainTitle()"
              (optionClickedEvent)="onChainDropdownSelect($event)">
            </entry-dropdown>
          }
          <div class="label-tooltip-seq" style="display: inline">
            @if (isCopySequence()) {
              <span class="label-tooltip-seq-text">Sequence copied</span>
            }
            <button (click)="copySequence()">Copy sequence</button>
          </div>
        </div>

        <div class="sequence-container">
          <div class="fasta-title">{{ fastaTitle() }}</div>
          <div class="numbering-note">
            Note: Residue numbering in the sequence viewer follows UniProt, while literature-derived annotations may use
            author-defined numbering; although computational mapping aligns these to the UniProt reference sequence,
            sequence offsets can still occur.
          </div>
          @if (activeSequence() && !plddtLoadAttempted()) {
            <div class="sequence-loading">
              <span style="display: inline-block; width: 20px;">
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
                    class="spinner_aj0A"
                    stroke="#707372"
                    fill="#707372" />
                </svg>
              </span>
            </div>
          }
          @if (activeSequence() && plddtLoadAttempted()) {
            <lib-smart-seq-viewer
              containerId="smart-seq-viewer-text-annotations"
              class="sequence"
              [sequence]="activeSequence()"
              [entityId]="activeEntityId()"
              [chainId]="activeChainId()"
              [backgroundDataInput]="backgroundDataInput()"
              [circleAboveDataInput]="circleAboveDataInput()"
              [isNucleic]="false"
              [options]="viewerOptions"
            ></lib-smart-seq-viewer>
          }
        </div>
        <!-- <p class="residue-info">Residues are colour-coded according to the availability of text-mined annotations.</p> -->

        @if (residueFilter() !== undefined) {
          <p class="residue-filter-info">
            Showing annotations for residue {{ residueFilter() }}.
            <span class="residue-action-button" (click)="clearResidueFilter()">Show all</span>
          </p>
        }

        <section class="chart-area">
          <section class="table-wrapper">
            <div class="table-search-row">
              <button
                type="button"
                class="annotations-download-link"
                [disabled]="searchedAnnotations().length === 0"
                (click)="downloadTableCsv()">
                Download annotations (csv)
                <img src="assets/img/download-nested.svg" alt="" width="18" height="18" />
              </button>
              <div style="position: relative;">
                <input
                  #tableSearchBox
                  class="vf-form__input searchLine"
                  autocomplete="off"
                  placeholder="Search the table"
                  [value]="tableSearchTerm()"
                  (input)="onSearchInput(tableSearchBox.value)"
                />
                @if (tableSearchBox.value === '') {
                  <span class="filtericon">
                    <img src="assets/img/Search.svg" alt="search" width="12" height="13" />
                  </span>
                }
                @if (tableSearchBox.value !== '') {
                  <span class="filtericon" (click)="onSearchInput('')">
                    <img src="assets/img/crossicon.svg" alt="clear" width="10" height="11" />
                  </span>
                }
              </div>
            </div>

            <table mat-table [dataSource]="dataSource" class="llmAnnotationsTable">
              <ng-container matColumnDef="annotations">
                <th mat-header-cell *matHeaderCellDef>
                  <div class="table-header-title">
                    Annotations ({{ searchedAnnotations().length }})
                    <help-icon-with-tooltip
                      style="margin-left: 4px;"
                      [tooltipText]="'Annotations here mean named entities for specific residues and point mutations identified in publication text. The named entity recognition was carried out with our published model described in Vollmar et al. (2024), PMID: 39333508.'"
                      [isOnlytext]="false" />
                  </div>
                  <p class="table-header-caption">
                    All annotations were text-mined with a confidence score &ge;{{ HIGH_CONFIDENCE_THRESHOLD }}. Exceptions with lower scores are indicated with a Low score symbol.
                  </p>
                </th>
                <td mat-cell *matCellDef="let annotation">
                  @if (isLicenseRestricted(annotation)) {
                    <span class="copyright-restricted-message">{{ COPYRIGHT_RESTRICTED_MESSAGE }}</span>
                  } @else {
                    <span [innerHTML]="annotation | highlightedSentence:isSentenceExpanded(annotation)" (click)="filterByResidue(annotation)"></span>
                    @if (sentenceNeedsTruncation(annotation)) {
                      <a class="vf-link sentence-toggle-link" (click)="toggleSentenceExpanded(annotation)">
                        {{ isSentenceExpanded(annotation) ? 'Show less' : 'Show more' }}
                      </a>
                    }
                    @if (isLowScore(annotation)) {
                      <div class="low-score-badge">
                        <svg class="low-score-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 8L12 13L18 8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M6 14L12 19L18 14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Low score: {{ roundScore(annotation.aiScore) }}
                      </div>
                    }
                  }
                </td>
              </ng-container>

              <ng-container matColumnDef="publication">
                <th mat-header-cell *matHeaderCellDef>
                   <div class="table-header-title">
                    Publication
                  <help-icon-with-tooltip
                    style="margin-left: 4px;"
                    [tooltipText]="'The column &quot;Publication&quot; contains details about the publication from which the annotation was mined. The first author is given with family name and first name initials followed by the publication year. Additionally, the PubMed ID for the publication is provided.'"
                    [isOnlytext]="false" />
                   </div>
                </th>
                <td mat-cell *matCellDef="let annotation" class="publication-cell">
                  @if (getAuthorYear(annotation); as authorYear) {
                    <div class="publication-authors">
                      @if (authorYear.author) {
                        <div>{{ authorYear.author }}</div>
                      }
                      @if (authorYear.year) {
                        <div>{{ authorYear.year }}</div>
                      }
                    </div>
                  }
                  <div class="publication-pmid">PMID: {{ annotation.pubmedId }}</div>
                  @if (getArticleLink(annotation)) {
                    <div class="publication-link">
                      <a [href]="getArticleLink(annotation)" target="_blank" class="publication-link-anchor">
                        View in article
                        <img src="assets/img/ex-link-open.svg" alt="" width="14" height="14" />
                      </a>
                    </div>
                  }
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
              <tr class="mat-row no-annotations-row" *matNoDataRow>
                @if (activeChainAnnotationsLoading()) {
                  <td class="mat-cell table-annotations-loading" [attr.colspan]="displayedColumns.length" aria-label="Loading annotations">
                      <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z" class="spinner_aj0A" stroke="#707372" fill="#707372" />
                      </svg>
                  </td>
                } @else if (residueFilter() !== undefined) {
                  <td class="mat-cell residue-no-annotations" [attr.colspan]="displayedColumns.length">
                    Residue not described in literature; no annotations found.
                  </td>
                } @else if (inRangeAnnotations().length === 0) {
                  <td class="mat-cell residue-no-annotations" [attr.colspan]="displayedColumns.length">
                    No annotations available for this chain's residue range.
                  </td>
                }
              </tr>
            </table>

            @if (searchedAnnotations().length > 0) {
              <mat-paginator [pageSize]="5" [pageSizeOptions]="[5, 10, 20]" aria-label="Select page"></mat-paginator>
            }
          </section>

          @if (entryData()) {
            <div class="container-3d">
              <div class="text-annotations-molstar-container"></div>
              <div class="text-annotations-paecontainer">
                @if (entryData().paeDocUrl) {
                  <app-pae-view
                    [paeid]="'text-annotations-paeviewer'"
                    (selectedRegion)="cmSelectionUpdate($event)">
                  </app-pae-view>
                }
              </div>
            </div>
          } @else {
            <div class="container-3d"></div>
          }
        </section>
      </div>
    </main>
  </div>
}
